<script setup lang="ts">
import type { BadgeProps, ButtonProps } from '#ui/types';
import type { RouteLocationRaw } from '#vue-router';

type IconBgButtonColor = Extract<ButtonProps['color'], 'primary' | 'secondary'>;
type UiProp = Partial<Record<keyof NonNullable<BadgeProps['ui']>, string>>;

// PROPS
const { color, ui } = defineProps({
    label: { type: String as PropType<TypedTranslateReturn>, required: true },
    size: { type: String as PropType<ButtonProps['size']>, default: undefined },
    color: { type: String as PropType<IconBgButtonColor>, default: 'primary' },
    icon: { type: String, default: 'lucide:arrow-right' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    to: { type: String as PropType<RouteLocationRaw>, default: undefined },
    ui: { type: Object as PropType<UiProp>, default: undefined },
});

// COMPOSABLES
const { ui: appConfigUi } = useAppConfig();

// DATA
const iconBgColor = computed(() => {
    switch (color) {
        case 'primary':
            return 'white';
        case 'secondary':
            return 'primary-dark';
    }
});
const iconBgClass = computed<string>(() => getBgClass(iconBgColor.value));
const iconClass = computed<string>(() => getTextClass(iconBgColor.value));
const { loading: loadingIcon } = appConfigUi.icons;

const mergedUi = computed(() => mergeUi({ base: 'justify-between text-center', label: 'whitespace-normal' }, ui ?? {}));
</script>

<template>
    <UButton :size="size" :color="color" :label="label" trailing :disabled="disabled" :loading="loading" :block="block" :ui="mergedUi" :to="to">
        <template v-if="block" #leading>
            <!-- Invisibile mock content with same icon size, to handle alignment in block mode -->
            <span class="flex p-1">
                <UIcon name="lucide:arrow-right" class="invisible" />
            </span>
        </template>
        <template #trailing>
            <span class="flex items-center justify-center rounded-full p-1" :class="iconBgClass">
                <UIcon :name="loading ? loadingIcon : 'lucide:arrow-right'" class="shrink-0" :class="iconClass" />
            </span>
        </template>
    </UButton>
</template>
